import React, { useEffect, useMemo } from "react";
import { Button, message, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  renderMaterialItemsSituations,
  renderMaterialItemsStatuses,
} from "shared/helpers";
import {
  ScannedItem,
  useExportScannedItemsExcelMutation,
  useGetScannedItemsQuery,
  useScanSerialNumberMutation,
} from "shared/api";
import { useNavigate } from "react-router-dom";
import { renderPaginationTotal } from "shared/ui";

import { useDebouncedTableFilter, usePagination } from "shared/hooks";

import { MobileCard } from "./ScanView/MobileCard";
import { MobilePagination } from "./ScanView/MobilePagination";
import { MobileSpinner } from "./ScanView/MobileLoader";
import { SearchInput } from "./ScanView/SearchInput";
import { getResultStatus } from "./ScanView/getResultStatus";

dayjs.extend(utc);
dayjs.extend(timezone);

export const ScanView: React.FC = () => {
  const navigate = useNavigate();
  const inputRef = React.useRef<HTMLInputElement>(null);

  const themeColor = JSON.parse(
    localStorage.getItem("color-theme") || '"#002F5F"'
  );

  const userTimezone = localStorage.getItem("timezone") || "UTC";

  const [scanStatus, setScanStatus] = React.useState<
    null | "found" | "not-found"
  >(null);

  const flashStatus = (type: "found" | "not-found") => {
    setScanStatus(type);
    setTimeout(() => setScanStatus(null), 1200);
  };

  const { offset, limit, pageSize, currentPage, setCurrentPage, setPageSize } =
    usePagination(100);

  const [xlsExport, { isLoading: isExporting }] =
    useExportScannedItemsExcelMutation();

  const {
    debouncedValue: debouncedSerialNumberValue,
    handleFilterChange: handleSerialNumberValue,
    value: serialNumberValue,
    clearFilterValue: clearSerialNumberValue,
  } = useDebouncedTableFilter("SerialNumber", 400);

  const { data, isFetching } = useGetScannedItemsQuery({
    expand:
      "material_item.material_stock_batch.material.material_category,user",
    offset,
    limit,
    ordering: "-timestamp",
  });

  const items = data?.results ?? [];

  const [scanSerial] = useScanSerialNumberMutation();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && serialNumberValue.trim()) {
      handleSerialNumberValue(serialNumberValue);
    }
  };

  const handleExportAllXLS = async () => {
    if (!items.length) {
      message.error("No scanned items found");
      return;
    }

    try {
      const blob = await xlsExport({
        count: items.length,
      }).unwrap();

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "export_scanned_items.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      message.error("Export error");
    }
  };

  useEffect(() => {
    if (!debouncedSerialNumberValue) return;

    scanSerial({ serial_number: debouncedSerialNumberValue })
      .unwrap()
      .then((res) => {
        flashStatus(res.result === "FOUND" ? "found" : "not-found");
      })
      .catch(() => flashStatus("not-found"));
  }, [debouncedSerialNumberValue]);

  useEffect(() => {
    if (window.innerWidth < 768 && isFetching) {
      window.scrollTo({ top: 0 });
    }
  }, [isFetching]);

  const columns: ColumnsType<ScannedItem> = useMemo(
    () => [
      {
        title: "Serial Number",
        dataIndex: "serial_number",
      },
      {
        title: "Material",
        render: (_, record) =>
          record.material_item?.material_stock_batch?.material?.name ?? "-",
      },
      {
        title: "Category",
        render: (_, record) =>
          record.material_item?.material_stock_batch?.material
            ?.material_category?.name ?? "-",
      },
      {
        title: "Quantity",
        render: (_, record) =>
          record.material_item?.calc_total_remaining_quantity ?? "-",
      },
      {
        title: "Last Scanned",
        render: (_, record) =>
          dayjs(
            record.material_item?.last_timestamp_scanned ?? record.timestamp
          )
            .tz(userTimezone)
            .format("DD MMM YYYY, HH:mm"),
      },
      {
        title: "Status",
        render: (_, record) =>
          record.material_item
            ? renderMaterialItemsStatuses(record.material_item)
            : "-",
      },

      {
        title: "Situation",
        render: (_, record) =>
          record.material_item
            ? renderMaterialItemsSituations(record.material_item)
            : "-",
      },
      {
        title: "User",
        render: (_, record) =>
          `${record.user.first_name} ${record.user.last_name}`,
      },
      {
        title: "Scan Status",
        render: (_, record) =>
          record.is_error ? (
            <span style={{ color: "red", fontWeight: 600 }}>Error</span>
          ) : (
            <span style={{ color: "green", fontWeight: 600 }}>OK</span>
          ),
      },
      {
        title: "Result",
        dataIndex: "result",
        render: (value: string) => {
          const { label, className } = getResultStatus(value);
          return <span className={className}>{label}</span>;
        },
      },
    ],
    [userTimezone]
  );

  return (
    <div className="w-full pb-10">
      <div className="w-full flex justify-between ">
        <div className="w-full justify-between items-center flex my-2 mx-3 max-md:mx-1 max-md:items-start">
          <div className="w-2/3 flex gap-3 items-center max-md:flex-col max-md:items-start">
            <SearchInput
              ref={inputRef}
              value={serialNumberValue}
              onChange={handleSerialNumberValue}
              onClear={clearSerialNumberValue}
              onKeyDown={handleKeyDown}
            />
            <Button
              type="primary"
              loading={isExporting}
              onClick={handleExportAllXLS}
              className="flex items-center gap-2"
              style={{
                backgroundColor: "#217346",
                borderColor: "#217346",
              }}
              disabled={!items.length}
            >
              <img
                src="/xls-icon.png"
                alt="xls-icon"
                style={{ width: 22, height: 22, objectFit: "contain" }}
              />
              XLS
            </Button>
          </div>

          <Button
            style={{ backgroundColor: themeColor }}
            type="primary"
            onClick={() => navigate("/material-management")}
          >
            Go back
          </Button>
        </div>
      </div>

      <div className="hidden lgPlus:block">
        {scanStatus && (
          <div
            className={`fixed top-1/2 left-1/2 -translate-x-1/2 z-50 w-20 h-20 rounded-full flex items-center justify-center text-white text-xl font-semibold animate-blink-3 ${
              scanStatus === "found" ? "bg-green-500" : "bg-red-500"
            }`}
          >
            {scanStatus === "found" ? "OK" : "NO"}
          </div>
        )}

        <Table<ScannedItem>
          rowKey="id"
          dataSource={items}
          columns={columns}
          loading={isFetching}
          pagination={{
            pageSize,
            current: currentPage,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100", "1000"],
            onShowSizeChange: (_, size) => {
              setPageSize(size);
              setCurrentPage(1);
            },
            onChange: (page) => setCurrentPage(page),
            total: data?.count,
            showTotal: renderPaginationTotal,
          }}
        />
      </div>

      <div className="lgPlus:hidden space-y-3 relative min-h-screen pb-7">
        {isFetching && <MobileSpinner />}

        {!isFetching &&
          items.map((item) => <MobileCard key={item.id} item={item} />)}

        {!isFetching && (
          <MobilePagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalCount={data?.count ?? 0}
            onPageChange={setCurrentPage}
          />
        )}

        {scanStatus && (
          <div
            className={`fixed top-1/2 left-1/2 -translate-x-1/2 z-50 w-20 h-20 rounded-full flex items-center justify-center text-white text-xl font-semibold animate-blink-3 ${
              scanStatus === "found" ? "bg-green-500" : "bg-red-500"
            }`}
          >
            {scanStatus === "found" ? "OK" : "NO"}
          </div>
        )}
      </div>
    </div>
  );
};
